/**
 * AU display helpers — every date in the UI shows ``dd-mm-yyyy``.
 *
 * Inputs are the ISO strings the backend produces (``YYYY-MM-DD`` for
 * dates, ``YYYY-MM-DDTHH:MM:SS[.fff][Z|±HH:MM]`` for datetimes).
 *
 * TIMEZONE MODEL
 * --------------
 * Timestamps are stored in UTC everywhere (DB, API) and converted to the
 * ORGANISATION's timezone here, at render time. Nothing is ever stored
 * in local time, so changing the org timezone re-renders history rather
 * than rewriting it.
 *
 * The zone is module state, set once from the loaded organisation (see
 * ``setDisplayTimeZone`` / ``useDisplayTimeZone``). That keeps these as
 * plain functions — usable from non-React code and from the hundreds of
 * existing call sites — instead of forcing every caller to become a hook
 * consumer and thread a timezone through.
 *
 * DATES vs INSTANTS — the important distinction:
 *
 *  * ``formatDateDMY`` handles CALENDAR DATES (date of birth, LMP, study
 *    date). These are not instants; they have no timezone. It does a
 *    literal text rearrangement and deliberately does NOT convert — a
 *    date-of-birth must never shift a day because the viewer's clock or
 *    the clinic's zone says so.
 *  * ``formatDateTimeDMY*`` handle INSTANTS (created_at, signed_at,
 *    audit timestamps). These DO convert into the org timezone.
 *
 * Returns the original string if it doesn't look like an ISO date —
 * callers shouldn't have to special-case "—" / placeholder values
 * coming from elsewhere.
 */

// Module-level display zone. ``null`` = fall back to the viewer's
// browser clock, which is the pre-existing behaviour and the only sane
// default before the organisation has loaded.
let displayTimeZone: string | null = null;

/**
 * Set the timezone every datetime in the UI renders in. Called once
 * when the organisation loads; safe to call repeatedly with the same
 * value. Pass null/empty to fall back to the browser clock.
 */
export function setDisplayTimeZone(tz: string | null | undefined): void {
  const next = (tz ?? "").trim() || null;
  if (next === displayTimeZone) return;
  if (next !== null && !isValidTimeZone(next)) {
    // A zone the browser's ICU build doesn't know would make every
    // Intl call throw. Refuse it and keep the previous (or browser)
    // clock rather than breaking every timestamp on the page.
    console.warn(`[dateFormat] unknown timezone "${next}"; keeping previous`);
    return;
  }
  displayTimeZone = next;
}

export function getDisplayTimeZone(): string | null {
  return displayTimeZone;
}

function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Break an instant into zone-aware parts. ``hourCycle: "h23"`` (not
 * ``hour12: false``) because the latter renders midnight as "24" in
 * some ICU builds.
 */
function zonedParts(d: Date): Record<string, string> {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    ...(displayTimeZone ? { timeZone: displayTimeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const out: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) out[p.type] = p.value;
  return out;
}

/** Calendar date — NO timezone conversion. See the note above. */
export function formatDateDMY(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Instant -> ``dd-mm-yyyy HH:MM`` in the organisation's timezone. */
export function formatDateTimeDMY(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = zonedParts(d);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}`;
}

/** Instant -> ``dd-mm-yyyy HH:MM:SS`` in the organisation's timezone. */
export function formatDateTimeDMYSeconds(
  iso: string | null | undefined,
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = zonedParts(d);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}:${p.second}`;
}

/** Instant -> ``HH:MM`` in the organisation's timezone. */
export function formatTimeHM(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = zonedParts(d);
  return `${p.hour}:${p.minute}`;
}

/**
 * The org timezone's current abbreviation (e.g. "AEST"), for labelling
 * a clock or a column header so a viewer in another zone knows which
 * clock they are reading. Empty string when no zone is configured.
 */
export function timeZoneAbbreviation(at: Date = new Date()): string {
  if (!displayTimeZone) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: displayTimeZone,
      timeZoneName: "short",
    }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * The calendar date (``YYYY-MM-DD``) of an instant AS SEEN in the org
 * timezone. Needed for "group by day" logic — grouping on the raw UTC
 * date would split a Brisbane working day at 10:00 local.
 */
export function zonedDateKey(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = zonedParts(d);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Escape hatch for surfaces with their own Intl format (short month
 * names, date-only renderings) that still must land on the clinic
 * clock. Same contract as the helpers above: pass an instant, get the
 * display zone applied. Returns the input unchanged when unparseable.
 */
export function formatInZone(
  iso: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = "en-GB",
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    ...(displayTimeZone ? { timeZone: displayTimeZone } : {}),
    ...options,
  }).format(d);
}

/** Offset (ms) of the display zone at a given instant: zone - UTC. */
function zoneOffsetMs(at: Date): number {
  if (!displayTimeZone) return -at.getTimezoneOffset() * 60_000;
  const p = zonedParts(at);
  // Carry the instant's own milliseconds through: the formatted parts
  // are second-granular, and dropping them would skew the offset by up
  // to 999 ms — enough to push a day-end bound into the next day.
  // Zone offsets are whole minutes, so ms are zone-independent.
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
    at.getUTCMilliseconds(),
  );
  return asIfUtc - at.getTime();
}

/**
 * Convert a wall-clock time IN THE DISPLAY ZONE to the matching UTC
 * instant, as an ISO string.
 *
 * Used by date-range filters: the user picks "27 July" meaning the
 * clinic's 27 July, but the API filters on UTC instants. Building the
 * bound with ``new Date("2026-07-27T00:00:00")`` uses the BROWSER's
 * clock, so in Brisbane the query would start 10 hours late and silently
 * drop the first third of the day.
 *
 * The offset is resolved twice because the offset itself depends on the
 * instant (DST): the first pass gets us close enough that the second
 * lands on the correct side of any transition.
 */
function zonedWallTimeToUtcIso(
  y: number, mo: number, d: number,
  hh: number, mi: number, ss: number, ms = 0,
): string {
  const guess = Date.UTC(y, mo - 1, d, hh, mi, ss, ms);
  const pass1 = guess - zoneOffsetMs(new Date(guess));
  const exact = guess - zoneOffsetMs(new Date(pass1));
  return new Date(exact).toISOString();
}

/** Start of a ``YYYY-MM-DD`` day in the display zone, as a UTC ISO instant. */
export function zonedDayStartUtcIso(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return new Date(`${dateStr}T00:00:00`).toISOString();
  return zonedWallTimeToUtcIso(+m[1]!, +m[2]!, +m[3]!, 0, 0, 0, 0);
}

/** End of a ``YYYY-MM-DD`` day in the display zone, as a UTC ISO instant. */
export function zonedDayEndUtcIso(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return new Date(`${dateStr}T23:59:59.999`).toISOString();
  return zonedWallTimeToUtcIso(+m[1]!, +m[2]!, +m[3]!, 23, 59, 59, 999);
}
